import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Sparkles, MoreHorizontal, Edit2, Folder, Archive, Trash2, Share, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTypingAnimation } from '../../hooks/useTypingAnimation';
import chatService from '../../services/chatService';
import { useAuth } from '../../contexts/AuthContext';

// Component to animate chat title typing
function AnimatedChatTitle({ chat, isSelected, previousTitle }) {
  const title = chat.title || 'Untitled Chat';
  // Only animate if previous title was "New Chat" and new title is different
  const shouldAnimate = previousTitle === 'New Chat' && title !== 'New Chat' && title !== previousTitle;
  const displayedText = useTypingAnimation(title, 25, shouldAnimate);

  return (
    <p className={`text-xs truncate ${
      isSelected ? 'text-purple-900 font-medium' : 'text-gray-700 group-hover:text-gray-900'
    }`}>
      {displayedText}
    </p>
  );
}

export default function ChatHistoryList({ 
  chats = [], 
  currentChatId,
  onChatSelect,
  onPinChat,
  onRenameChat,
  onMoveToProject,
  onArchiveChat,
  onDeleteChat,
  projects = [],
  searchQuery = '',
  pinnedMoments = [],
  onMomentSelect
}) {
  const [activeTab, setActiveTab] = useState('recent');
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [menuOpenChatId, setMenuOpenChatId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const { user } = useAuth();
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingMomentId, setEditingMomentId] = useState(null);
  const [editingMomentTitle, setEditingMomentTitle] = useState('');
  const menuRefs = useRef({});
  const buttonRefs = useRef({});
  const previousTitlesRef = useRef({});

  // Track title changes for animation
  useEffect(() => {
    chats.forEach(chat => {
      const previousTitle = previousTitlesRef.current[chat.id];
      if (previousTitle && previousTitle !== chat.title && previousTitle === 'New Chat') {
        // Title changed from "New Chat" to something else - will trigger animation
        console.log(`📝 Title changed for chat ${chat.id}: "${previousTitle}" → "${chat.title}"`);
      }
      previousTitlesRef.current[chat.id] = chat.title;
    });
  }, [chats]);

  // Filter chats/moments based on active tab and search query
  // Moments tab shows saved moments
  const filteredChats = activeTab === 'recent' ? chats.filter(chat => {
    // In Recent tab: show ALL chats (both with and without folders)
    // Chats in folders will also appear in their respective folders
    
    if (!searchQuery) {
      return true; // Show all chats when no search query
    }
    
    const query = searchQuery.toLowerCase();
    
    // Search in title
    if (chat.title?.toLowerCase().includes(query)) {
      return true;
    }
    
    // Search in message content
    if (chat.messages && Array.isArray(chat.messages)) {
      const messageText = chat.messages
        .map(msg => msg.content || '')
        .join(' ')
        .toLowerCase();
      if (messageText.includes(query)) {
        return true;
      }
    }
    
    // Search in project/collection name
    if (chat.folder?.name?.toLowerCase().includes(query)) {
      return true;
    }
    
    return false;
  }) : [];

  // For Moments tab, filter moments
  const filteredMoments = activeTab === 'pinned' ? pinnedMoments.filter(moment => {
    if (!searchQuery) {
      return true;
    }
    
    const query = searchQuery.toLowerCase();
    
    // Search in title
    if (moment.title?.toLowerCase().includes(query)) {
      return true;
    }
    
    // Search in message content
    if (moment.message_content?.toLowerCase().includes(query)) {
      return true;
    }
    
    // Search in conversation title
    if (moment.conversation?.title?.toLowerCase().includes(query)) {
      return true;
    }
    
    return false;
  }) : [];

  // Sort chats by updated_at
  const sortedChats = [...filteredChats].sort((a, b) => {
    return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
  });

  // Sort moments by created_at (most recent first)
  const sortedMoments = [...filteredMoments].sort((a, b) => {
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.values(menuRefs.current).forEach(ref => {
        if (ref && !ref.contains(event.target)) {
          setMenuOpenChatId(null);
        }
      });
    };

    if (menuOpenChatId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpenChatId]);

  // Close menu on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMenuOpenChatId(null);
        setEditingChatId(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);


  const handleMenuClick = (e, chatId) => {
    e.stopPropagation();
    if (buttonRefs.current[chatId]) {
      const rect = buttonRefs.current[chatId].getBoundingClientRect();
      setMenuPosition({
        top: rect.top,
        left: rect.right
      });
    }
    setMenuOpenChatId(menuOpenChatId === chatId ? null : chatId);
  };

  const handleRename = (chat) => {
    setEditingChatId(chat.id);
    setEditingTitle(chat.title || '');
    setMenuOpenChatId(null);
  };

  const handleRenameSubmit = (chatId) => {
    if (editingTitle.trim() && onRenameChat) {
      onRenameChat(chatId, editingTitle.trim());
    }
    setEditingChatId(null);
    setEditingTitle('');
  };

  const handleMoveToProject = (chatId, projectId) => {
    if (onMoveToProject) {
      onMoveToProject(chatId, projectId);
    }
    setMenuOpenChatId(null);
  };

  const handleArchive = (chatId) => {
    if (onArchiveChat) {
      onArchiveChat(chatId);
    }
    setMenuOpenChatId(null);
  };

  const handleDelete = (chatId) => {
    if (window.confirm('Are you sure you want to delete this chat?')) {
      if (onDeleteChat) {
        onDeleteChat(chatId);
      }
    }
    setMenuOpenChatId(null);
  };

  return (
    <div className="mb-3">
      {/* Tabs */}
      <div className="flex items-center gap-1.5 mb-3">
        <button
          onClick={() => setActiveTab('recent')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            activeTab === 'recent'
              ? 'bg-gray-200/80 text-gray-900'
              : 'bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
          }`}
        >
          <MessageSquare size={12} />
          <span>Recent</span>
        </button>
        <button
          onClick={() => setActiveTab('pinned')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all relative ${
            activeTab === 'pinned'
              ? 'bg-gray-200/80 text-gray-900'
              : 'bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
          }`}
        >
          <Sparkles size={12} />
          <span>Moments</span>
          {pinnedMoments.length > 0 && (
            <span className="ml-1 px-1 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded-full min-w-[16px] text-center">
              {pinnedMoments.length}
            </span>
          )}
        </button>
      </div>

      {/* Chat List or Moments List */}
      <div className="space-y-0.5">
        {activeTab === 'pinned' ? (
          // Show moments
          sortedMoments.length === 0 ? (
            <div className="px-2 py-3">
              <p className="text-xs text-gray-400 text-center">
                {searchQuery ? 'No moments found' : 'No moments yet'}
              </p>
            </div>
          ) : (
            sortedMoments.map((moment) => {
              const isSelected = currentChatId === moment.conversation_id;
              // Use message_content as snippet, or generate from title
              const snippet = moment.message_content 
                ? (moment.message_content.length > 80 
                    ? moment.message_content.substring(0, 80) + '...' 
                    : moment.message_content)
                : moment.title || 'Untitled Moment';

              return (
                <div
                  key={moment.id}
                  onClick={() => {
                    if (onMomentSelect && moment.conversation_id) {
                      onMomentSelect(moment.conversation_id, moment.message_id);
                    }
                  }}
                  className={`relative flex items-start gap-2 px-2 py-1.5 rounded-lg transition-colors cursor-pointer group ${
                    isSelected 
                      ? 'bg-purple-100/60 hover:bg-purple-100/80' 
                      : 'hover:bg-gray-100/60'
                  }`}
                >
                  <Sparkles size={12} className="text-purple-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    {editingMomentId === moment.id ? (
                      <input
                        type="text"
                        value={editingMomentTitle}
                        onChange={(e) => setEditingMomentTitle(e.target.value)}
                        onBlur={async () => {
                          if (editingMomentTitle.trim() && user) {
                            const result = await chatService.renamePinnedMoment(user.id, moment.id, editingMomentTitle.trim());
                            if (result.data) {
                              setEditingMomentId(null);
                              setEditingMomentTitle('');
                              // Refresh moments
                              const momentsResult = await chatService.getUserPinnedMoments(user.id);
                              if (momentsResult.data) {
                                // Update parent component's moments
                                // This will be handled by parent component refresh
                              }
                            }
                          }
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            if (editingMomentTitle.trim() && user) {
                              const result = await chatService.renamePinnedMoment(user.id, moment.id, editingMomentTitle.trim());
                              if (result.data) {
                                setEditingMomentId(null);
                                setEditingMomentTitle('');
                              }
                            }
                          } else if (e.key === 'Escape') {
                            setEditingMomentId(null);
                            setEditingMomentTitle('');
                          }
                        }}
                        autoFocus
                        className="w-full text-xs text-gray-700 bg-white border border-purple-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <>
                        <p className={`text-xs truncate ${
                          isSelected ? 'text-purple-900 font-medium' : 'text-gray-700 group-hover:text-gray-900'
                        }`}>
                          {moment.title || 'Untitled Moment'}
                        </p>
                        <p className="text-[10px] text-gray-500 line-clamp-2 mt-0.5">
                          {snippet}
                        </p>
                        {moment.conversation?.title && (
                          <p className="text-[10px] text-gray-400 truncate mt-0.5">
                            {moment.conversation.title}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingMomentId(moment.id);
                      setEditingMomentTitle(moment.title || '');
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200/80 transition-all text-gray-400"
                    title="Rename"
                  >
                    <Edit2 size={10} />
                  </button>
                </div>
              );
            })
          )
        ) : (
          // Show recent chats
          sortedChats.map((chat) => {
            const isHovered = hoveredChatId === chat.id;
            const isMenuOpen = menuOpenChatId === chat.id;
            const isEditing = editingChatId === chat.id;
            const isSelected = currentChatId === chat.id;

            return (
              <div
                key={chat.id}
                onMouseEnter={() => setHoveredChatId(chat.id)}
                onMouseLeave={() => setHoveredChatId(null)}
                onClick={() => !isEditing && onChatSelect && onChatSelect(chat.id)}
                className={`relative flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors cursor-pointer group ${
                  isSelected 
                    ? 'bg-purple-100/60 hover:bg-purple-100/80' 
                    : 'hover:bg-gray-100/60'
                }`}
              >
              {isEditing ? (
                <input
                  type="text"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onBlur={() => handleRenameSubmit(chat.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRenameSubmit(chat.id);
                    } else if (e.key === 'Escape') {
                      setEditingChatId(null);
                      setEditingTitle('');
                    }
                  }}
                  autoFocus
                  className="flex-1 text-xs text-gray-700 bg-white border border-purple-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <>
                  {/* Text - takes remaining space */}
                  <div className="flex-1 min-w-0">
                    <AnimatedChatTitle
                      chat={chat}
                      isSelected={isSelected}
                      previousTitle={previousTitlesRef.current[chat.id]}
                    />
                  </div>

                  {/* Three dots menu - visible on hover, stays open when clicked */}
                  <div className={`relative transition-opacity flex-shrink-0 ${isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} ref={el => menuRefs.current[chat.id] = el}>
                    <button
                      ref={el => buttonRefs.current[chat.id] = el}
                      onClick={(e) => handleMenuClick(e, chat.id)}
                      className="p-1 rounded hover:bg-gray-200/80 transition-colors text-gray-400"
                      title="More options"
                    >
                      <MoreHorizontal size={12} />
                    </button>

                    {/* Context Menu - Fixed positioning to escape sidebar, opens to the right */}
                    <AnimatePresence>
                      {isMenuOpen && (
                        <>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[100]"
                            onClick={() => setMenuOpenChatId(null)}
                          />
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, x: -10 }}
                            animate={{ opacity: 1, scale: 1, x: 0 }}
                            exit={{ opacity: 0, scale: 0.95, x: -10 }}
                            transition={{ duration: 0.15 }}
                            className="fixed w-48 bg-white rounded-lg shadow-2xl border border-gray-200/50 z-[101] overflow-hidden"
                            style={{
                              top: `${menuPosition.top}px`,
                              left: `${menuPosition.left + 8}px`
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              <button
                                onClick={() => handleRename(chat)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Edit2 size={14} />
                                <span>Rename</span>
                              </button>
                              
                              <button
                                onClick={() => handleMoveToProject(chat.id, null)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Target size={14} />
                                <span>Move to Focus</span>
                              </button>

                              {projects.length > 0 && (
                                <div className="border-t border-gray-100">
                                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase">
                                    Move to Project
                                  </div>
                                  {projects.map(project => (
                                    <button
                                      key={project.id}
                                      onClick={() => handleMoveToProject(chat.id, project.id)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <Folder size={14} />
                                      <span className="truncate">{project.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}

                              <div className="border-t border-gray-100">
                                <button
                                  onClick={() => handleArchive(chat.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <Archive size={14} />
                                  <span>Archive</span>
                                </button>
                                
                                <button
                                  onClick={() => handleDelete(chat.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 size={14} />
                                  <span>Delete</span>
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              )}
              </div>
            );
          })
        )}
        
        {activeTab === 'recent' && sortedChats.length === 0 && (
          <div className="px-2 py-3">
            <p className="text-xs text-gray-400 text-center">
              {searchQuery ? 'No chats found' : 'No recent chats'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
